import { useApolloClient } from '@apollo/client';
import { useEffect, useRef } from 'react';

import {
  ChatDrawerScreen,
  useWebDrawer,
} from '@components/Web/Drawer/WebDrawerContext';
import {
  GetProjectDocument,
  GetProjectQuery,
  ProjectMemberRole,
  useCreateChatMutation,
} from '@graphql/generated';
import useActiveChat from '@hooks/useActiveChat';
import useMe from '@hooks/useMe';
import { useAppNavigation } from '@navigation/useAppNavigation';
import {
  clearLoginSucceeded,
  isWithinLoginWindow,
} from '@utils/loginSuccessMarker';

const QUEUE_SETTLE_MS = 1500;

/**
 * TA-10891: when a member signs in and, within the first minute, a
 * "You've been added to this project" card is shown, open their 1:1 chat
 * with the owner of the first (top) card's project — the chat the owner's
 * invite and task assignment land in. Runs at most once per sign-in: the
 * login marker is cleared as soon as the (settled) queue has been read.
 *
 * `projectId` is the top card's project id, or undefined when the queue is
 * empty.
 */
export const useOpenOwnerChatAfterLogin = (projectId: string | undefined) => {
  const client = useApolloClient();
  const { me } = useMe();
  const [createChat] = useCreateChatMutation();
  const { setIsOpenChat, setActiveMessageId } = useActiveChat();
  const { chatExpanded, chatListOpen, setChatListOpen, setShouldChatListOpen } =
    useWebDrawer();
  const { navigateToChatDrawer } = useAppNavigation();
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current || !me?.id || !projectId) return;
    if (!isWithinLoginWindow()) return;
    // Cards can arrive in more than one batch (the query, then the meUpdated
    // subscription), so let the queue settle before picking the top card.
    const timer = setTimeout(() => {
      if (handledRef.current || !isWithinLoginWindow()) return;
      handledRef.current = true;
      clearLoginSucceeded();
      openOwnerChat().catch(() => {
        /* best-effort — the onboarding card still offers the project */
      });
    }, QUEUE_SETTLE_MS);

    const openOwnerChat = async () => {
      const { data } = await client.query<GetProjectQuery>({
        query: GetProjectDocument,
        variables: { id: projectId },
        fetchPolicy: 'network-only',
      });
      const project = data?.getProject;
      const owner =
        project?.members?.find((m) => m.role === ProjectMemberRole.Owner)
          ?.user ?? project?.creator;
      if (!owner?.id || owner.id === me.id) return;

      // Only the owner — the backend adds the current user as chat owner.
      const result = await createChat({
        variables: { attributes: { userIds: [owner.id] } },
      });
      const chat = result.data?.createChat;
      if (!chat?.id) return;

      if (!chatExpanded && chatListOpen) {
        setShouldChatListOpen(true);
        setChatListOpen(false);
      }
      setIsOpenChat(true);
      setActiveMessageId?.(undefined);
      navigateToChatDrawer({
        screen: ChatDrawerScreen.details,
        activeChat: chat,
      });
    };

    return () => clearTimeout(timer);
  }, [me?.id, projectId]);
};
